import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

/**
 * Native (Capacitor) app integration. Everything here is a no-op on the web, so the game layer
 * can call it unconditionally.
 */
export const isNative = (): boolean => Capacitor.isNativePlatform();

/**
 * One-time native setup before the game starts. Android hides the status bar (full screen). iOS
 * keeps it, light text over the dark edge: hiding it changes the safe area while the web view is
 * starting, and WebKit could keep the old insets, so the HUD sat under the Dynamic Island and the
 * bottom bar ran off the screen (owner, TestFlight build 3). On notched and island iPhones the
 * status bar's strip is outside the safe area anyway.
 */
export async function initNativeShell(): Promise<void> {
  if (!isNative()) return;
  try {
    if (Capacitor.getPlatform() === 'ios') await StatusBar.setStyle({ style: Style.Dark });
    else await StatusBar.hide();
  } catch {
    // Not every platform supports it; the game still fits the safe area.
  }
}

/**
 * Calls `cb` with the link that opened the app (a playlawnmower.com link claimed by universal
 * links), when the app was already running or was started by it. Native only.
 */
export function onAppUrlOpen(cb: (url: string) => void): void {
  if (isNative()) void App.addListener('appUrlOpen', ({ url }) => cb(url));
}

/**
 * Calls `cb` when the app comes back to the foreground (natively; the page becoming visible).
 * Returns the way to stop (a screen listening only while it is up).
 */
export function onAppShown(cb: () => void): () => void {
  const onVisibility = () => {
    if (!document.hidden) cb();
  };
  document.addEventListener('visibilitychange', onVisibility);
  const handle = isNative()
    ? App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) cb();
      })
    : null;
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    void handle?.then((h) => h.remove());
  };
}

/**
 * Calls `cb` whenever the app goes to the background (home button, app switcher, a call). On the
 * web that is the page becoming hidden; natively the app lifecycle is the reliable signal.
 */
export function onAppHidden(cb: () => void): () => void {
  const onVisibility = () => {
    if (document.hidden) cb();
  };
  document.addEventListener('visibilitychange', onVisibility);
  const handle = isNative()
    ? App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) cb();
      })
    : null;
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    void handle?.then((h) => h.remove());
  };
}

/** Android's hardware back button (no-op elsewhere). */
export function onBackButton(cb: () => void): void {
  if (isNative()) void App.addListener('backButton', cb);
}

/**
 * Opens a web page over the game: an in-app browser natively (SFSafariViewController / Custom
 * Tabs), a new tab on the web.
 */
export function openUrl(url: string): void {
  if (isNative()) void Browser.open({ url });
  else window.open(url, '_blank', 'noopener');
}

/** Leaves the app (Android back from the main menu). */
export function exitApp(): void {
  if (isNative()) void App.exitApp();
}
